// Fixed-timestep game loop with interpolation
// Physics/netcode runs at 64Hz, rendering at display refresh rate

const SERVER_TICK_RATE = 64;
const TICK_DURATION = 1000 / SERVER_TICK_RATE; // 15.625ms

export class GameLoop {
  private accumulator = 0;
  private lastTime = 0;
  private running = false;
  private frameId = 0;

  constructor(
    private fixedUpdate: (dt: number) => void, // Physics/netcode at 64Hz
    private render: (alpha: number) => void,   // Rendering at display refresh rate
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.frameId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  private frame = (now: number): void => {
    if (!this.running) return;

    const delta = Math.min(now - this.lastTime, 100); // Cap to prevent spiral of death
    this.lastTime = now;
    this.accumulator += delta;

    // Fixed timestep updates
    let ticks = 0;
    while (this.accumulator >= TICK_DURATION && ticks < 4) {
      this.fixedUpdate(TICK_DURATION / 1000); // Pass dt in seconds
      this.accumulator -= TICK_DURATION;
      ticks++;
    }

    // If we're still behind after 4 ticks, clamp to one tick (preserves pending work)
    if (this.accumulator > TICK_DURATION * 4) {
      console.warn('Frame spike: accumulator clamped');
      this.accumulator = TICK_DURATION;
    }

    // Interpolation factor for rendering between ticks
    const alpha = this.accumulator / TICK_DURATION;
    this.render(alpha);

    this.frameId = requestAnimationFrame(this.frame);
  };
}
